/**
 * This browser's admin key (docs/product/admin-auth.md §4).
 *
 * A P-256 key pair made by WebCrypto with `extractable: false`: the private half never leaves the browser's key store,
 * not even to this page's own scripts — a script can ask it to sign while the page is open, but cannot copy it out and
 * use it elsewhere. The pair lives in IndexedDB (a CryptoKey is stored as an opaque handle, not as bytes). The public
 * half goes to the API at sign-in and is bound to the session; every request is then signed with the private half, so
 * a session token copied out of this browser is useless without it. There is no cookie.
 *
 * One pair per API (mainnet, Nile), made fresh at each sign-in to that API: signing in again leaves the old session's
 * key behind, and signing in to Nile never invalidates the mainnet session.
 */

export type KeySlot = "mainnet" | "nile";

const DB_NAME = "tenergy-admin";
const STORE = "browser-keys";

/** Where the pairs are kept: IndexedDB in a browser, a map where there is none (jsdom in tests). */
interface KeyStore {
  get(slot: string): Promise<CryptoKeyPair | undefined>;
  put(slot: string, pair: CryptoKeyPair): Promise<void>;
  delete(slot: string): Promise<void>;
}

function memoryStore(): KeyStore {
  const pairs = new Map<string, CryptoKeyPair>();
  return {
    get: async (slot) => pairs.get(slot),
    put: async (slot, pair) => void pairs.set(slot, pair),
    delete: async (slot) => void pairs.delete(slot),
  };
}

function idbStore(): KeyStore {
  let opened: Promise<IDBDatabase> | null = null;
  const open = () =>
    (opened ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => request.result.createObjectStore(STORE);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    }));
  const run = async <T>(mode: IDBTransactionMode, act: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
    const db = await open();
    return new Promise<T>((resolve, reject) => {
      const request = act(db.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  };
  return {
    get: (slot) => run("readonly", (store) => store.get(slot) as IDBRequest<CryptoKeyPair | undefined>),
    put: async (slot, pair) => void (await run("readwrite", (store) => store.put(pair, slot))),
    delete: async (slot) => void (await run("readwrite", (store) => store.delete(slot))),
  };
}

let store: KeyStore | null = null;
const keyStore = () => (store ??= typeof indexedDB === "undefined" ? memoryStore() : idbStore());

/** Test seam: start from an empty store. */
export function resetBrowserKeys(): void {
  store = null;
}

/**
 * Mock seam (tests, the mocked preview): mark `slot` as holding a key, synchronously. The mark cannot sign — a mocked
 * API checks no signature — and nothing outside src/mocks calls this.
 */
export function markMockBrowserKey(slot: KeySlot): void {
  void keyStore().put(slot, {} as CryptoKeyPair);
}

const ALGORITHM = { name: "ECDSA", namedCurve: "P-256" } as const;

export function base64url(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = "";
  for (const byte of view) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

const pendingOf = (slot: KeySlot) => `${slot}:pending`;

/**
 * A fresh pair for a sign-in under way, kept apart from `slot`'s current one until the sign-in succeeds
 * (`commitBrowserKey`). Other tabs go on signing with the current key and token meanwhile; replacing the key at once
 * left them signing an old token with the new key, which the API refused (2026-10-31). Returns SPKI DER, base64url.
 */
export async function prepareBrowserKey(slot: KeySlot): Promise<string> {
  const pair = await crypto.subtle.generateKey(ALGORITHM, false, ["sign", "verify"]);
  await keyStore().put(pendingOf(slot), pair);
  return base64url(await crypto.subtle.exportKey("spki", pair.publicKey));
}

/** The sign-in succeeded: its pair becomes `slot`'s key, together with the session token written right after. */
export async function commitBrowserKey(slot: KeySlot): Promise<void> {
  const pair = await keyStore().get(pendingOf(slot));
  if (pair === undefined) throw new Error("No sign-in key is waiting for this session");
  await keyStore().put(slot, pair);
  await keyStore().delete(pendingOf(slot));
}

/** Whether `slot` has a pair at all (a stored session without one cannot sign and is dropped). */
export async function hasBrowserKey(slot: KeySlot): Promise<boolean> {
  return (await keyStore().get(slot)) !== undefined;
}

export async function forgetBrowserKey(slot: KeySlot): Promise<void> {
  await keyStore().delete(slot);
}

/** ECDSA P-256 / SHA-256 over `text`; WebCrypto gives r‖s (IEEE P1363), which is what the API checks. */
export async function signWithBrowserKey(slot: KeySlot, text: string): Promise<string> {
  const pair = await keyStore().get(slot);
  if (pair === undefined) throw new Error("This browser has no admin key for this session");
  const signature = await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, pair.privateKey, new TextEncoder().encode(text));
  return base64url(signature);
}

export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function randomNonce(): string {
  return base64url(crypto.getRandomValues(new Uint8Array(16)));
}
